// src/components/membership/PublicMembershipSections.js
// Componentes públicos para mostrar membresías técnico ↔ empresa.
//   - UserCompaniesPublicSection: "Trabaja en" para perfiles de técnicos
//   - CompanyTeamPublicSection: "Equipo" para perfiles públicos de empresa
//
// Ambos consultan via RPCs públicas (sólo devuelven membresías active).
// Si no hay datos o la migración no se aplicó, no renderizan nada.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiBriefcase, FiUsers, FiMapPin } from 'react-icons/fi';
import companyMembersService from '../../services/companyMembersService';

// ─── Para perfil público de técnico (/u/:userId) ────────────────────
export const UserCompaniesPublicSection = ({ userId }) => {
  const [companies, setCompanies] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let active = true;
    companyMembersService.getUserCompanies(userId).then(res => {
      if (!active) return;
      if (res.ok) setCompanies(res.companies);
      setLoaded(true);
    });
    return () => { active = false; };
  }, [userId]);

  if (!loaded || companies.length === 0) return null;

  return (
    <div className="mt-8">
      <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
        <FiBriefcase className="text-primary-400" /> Trabaja en
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {companies.map(c => {
          const initial = (c.company_name?.[0] || 'E').toUpperCase();
          return (
            <Link
              key={c.company_id}
              to={`/empresa/${c.company_id}`}
              className="flex items-center gap-3 p-3 rounded-lg bg-dark-700/50 border border-dark-600 hover:border-primary-500 transition-colors"
            >
              {c.logo ? (
                <img src={c.logo} alt="" className="w-12 h-12 rounded-lg object-cover bg-dark-900" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-dark-600 flex items-center justify-center text-white font-semibold">
                  {initial}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium truncate">{c.company_name || 'Empresa'}</div>
                <div className="text-gray-400 text-xs truncate">
                  {c.role_in_company || 'Miembro del equipo'}
                  {(c.city || c.country) && (
                    <span className="text-gray-500"> · {[c.city, c.country].filter(Boolean).join(', ')}</span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

// ─── Para perfil público de empresa (/empresa/:slug) ────────────────
// Acepta tanto companyId (uuid) como un wrapper que carga el id a partir del slug.
export const CompanyTeamPublicSection = ({ companyId, accentColor }) => {
  const [team, setTeam] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    let active = true;
    companyMembersService.getCompanyTeam(companyId).then(res => {
      if (!active) return;
      if (res.ok) setTeam(res.team);
      setLoaded(true);
    });
    return () => { active = false; };
  }, [companyId]);

  if (!loaded || team.length === 0) return null;

  const title = 'Nuestro equipo';
  const accent = accentColor || '#6366f1';

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl font-bold mb-3 text-center" style={{ color: '#1E293B' }}>
          {title}
        </h2>
        <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
          Los profesionales que forman parte de nuestra red de trabajo.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {team.map(m => {
            const fullName = `${m.first_name || ''} ${m.last_name || ''}`.trim() || 'Técnico';
            const initial = (fullName[0] || 'T').toUpperCase();
            const link = m.is_public_profile ? `/u/${m.user_id}` : null;

            const content = (
              <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 border border-gray-200 hover:border-gray-300 transition-colors">
                {m.profile_image ? (
                  <img src={m.profile_image} alt={fullName}
                    className="w-14 h-14 rounded-full object-cover" />
                ) : (
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center text-white font-semibold text-xl"
                    style={{ background: accent }}
                  >
                    {initial}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate" style={{ color: '#1E293B' }}>{fullName}</div>
                  <div className="text-sm text-gray-600 truncate">
                    {m.role_in_company || m.job_title || 'Miembro del equipo'}
                  </div>
                </div>
              </div>
            );

            return link ? (
              <Link key={m.user_id} to={link} className="block">{content}</Link>
            ) : (
              <div key={m.user_id}>{content}</div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
